import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDemo } from "@/context/DemoContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import FeedbackButton from "@/components/FeedbackButton";

function useCrud(collection, reload) {
  const create = async (payload) => { await api.post(`/${collection}`, payload); await reload(); toast.success("Creato"); };
  const update = async (id, payload) => { await api.put(`/${collection}/${id}`, payload); await reload(); toast.success("Aggiornato"); };
  const remove = async (id) => { await api.del(`/${collection}/${id}`); await reload(); toast.success("Eliminato"); };
  return { create, update, remove };
}

function EntityDialog({ open, onOpenChange, title, fields, initial, onSave }) {
  const [form, setForm] = useState(initial || {});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setForm(initial || {}); setErrors({}); setBusy(false); }, [initial, open]);

  const validate = () => {
    const err = {};
    for (const f of fields) {
      const v = (form[f.key] || "").toString().trim();
      if (f.required && !v) err[f.key] = "Campo obbligatorio";
      if (f.type === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) err[f.key] = "E-mail non valida";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const doSave = async () => {
    if (busy) return;              // Previene doppio/triplo click → duplicati
    if (!validate()) return;
    setBusy(true);
    try { await onSave(form); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="entity-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(f => (
            <div key={f.key}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "select" ? (
                <Select
                  value={form[f.key] ? form[f.key] : "__none__"}
                  onValueChange={v => setForm({ ...form, [f.key]: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map(o => {
                      const optValue = o.value === "" ? "__none__" : o.value;
                      return <SelectItem key={optValue} value={optValue}>{o.label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              ) : f.type === "switch" ? (
                <div className="flex items-center gap-2"><Switch checked={!!form[f.key]} onCheckedChange={v => setForm({ ...form, [f.key]: v })} data-testid={`field-${f.key}`} /><span className="text-sm text-slate-600">{form[f.key] ? "Sì" : "No"}</span></div>
              ) : (
                <Input
                  type={f.type === "email" ? "email" : "text"}
                  value={form[f.key] || ""}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                  placeholder={f.type === "email" ? "nome@dominio.it" : undefined}
                />
              )}
              {errors[f.key] && <div className="text-xs text-red-600 mt-1" data-testid={`err-${f.key}`}>{errors[f.key]}</div>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Annulla</Button>
          <Button onClick={doSave} data-testid="entity-save" disabled={busy}>{busy ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportAnagraficaButton({ onDone }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setConfirmOpen(true);
  };

  const doImport = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const r = await fetch(`${API}/admin/import_anagrafica_xlsx`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ detail: "Errore" }));
        throw new Error(j.detail || "Errore import");
      }
      const j = await r.json();
      toast.success(`Import OK: ${j.clienti_importati} clienti, ${j.stabilimenti_importati} stabilimenti${j.stabilimenti_skipped ? ` (${j.stabilimenti_skipped} skippati)` : ""}`);
      await onDone();
      setConfirmOpen(false); setPendingFile(null);
    } catch (err) {
      toast.error(`Errore: ${err.message || err}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" onChange={handleFile} className="hidden" data-testid="import-anagrafica-input" />
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="btn-import-anagrafica">
        <Upload className="w-4 h-4 mr-1" /> Importa Anagrafica (Clienti + Stabilimenti)
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importa anagrafica — sostituzione completa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">File: <span className="font-mono">{pendingFile?.name}</span></div>
            <div className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded p-2">
              <strong>Attenzione:</strong> l'operazione <strong>elimina tutti i Clienti, gli Stabilimenti e i Contatti esistenti</strong> e li sostituisce con quelli del file.<br/>
              I ticket rimangono in database — quelli che referenziano clienti non più presenti mostreranno il nome originale come "storico".
            </div>
            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2">
              Formato atteso: file <strong>.xlsx</strong> con fogli <strong>"Clienti"</strong> e <strong>"Stabilimenti"</strong>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={doImport} disabled={busy} data-testid="btn-import-confirm" className="bg-red-700 hover:bg-red-800 text-white">{busy ? "Importazione…" : "Cancella tutto e importa"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ImportContattiButton({ onDone }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f); setConfirmOpen(true);
  };

  const doImport = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const r = await fetch(`${API}/admin/import_contatti_xlsx`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) { const j = await r.json().catch(() => ({ detail: "Errore" })); throw new Error(j.detail || "Errore import"); }
      const j = await r.json();
      toast.success(`Import OK: ${j.contatti_importati} contatti${j.righe_saltate ? ` (${j.righe_saltate} skippati)` : ""}`);
      await onDone();
      setConfirmOpen(false); setPendingFile(null);
    } catch (err) {
      toast.error(`Errore: ${err.message || err}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" onChange={handleFile} className="hidden" data-testid="import-contatti-input" />
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="btn-import-contatti" className="border-blue-500 text-blue-800 hover:bg-blue-50">
        <Upload className="w-4 h-4 mr-1" /> Importa Contatti
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importa contatti — sostituzione completa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">File: <span className="font-mono">{pendingFile?.name}</span></div>
            <div className="text-xs bg-red-50 border border-red-300 text-red-900 rounded p-2">
              <strong>Attenzione:</strong> elimina <strong>tutti i contatti esistenti</strong> e li sostituisce con quelli del file.
            </div>
            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded p-2">
              Formato atteso: file <strong>.xlsx</strong> con foglio <strong>"Contatti"</strong>.<br/>
              Colonne: <span className="font-mono">Codice cliente, Codice stabilimento, Livello contatto, Nome, Cognome, Ruolo, Email, Telefono, Principale?, Attivo?, Note</span>.<br/>
              I contatti si collegano tramite <strong>Codice cliente</strong> (obbligatorio) e opzionalmente <strong>Codice stabilimento</strong>.
              <br/><strong>Prima</strong> importa l'anagrafica clienti/stabilimenti.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={doImport} disabled={busy} data-testid="btn-import-contatti-confirm" className="bg-red-700 hover:bg-red-800 text-white">
              {busy ? "Importazione…" : "Cancella tutto e importa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ImportTicketStoriciButton({ onDone }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [mode, setMode] = useState("relink"); // "relink" (sicuro, default) | "replace" (distruttivo)

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingFile(f);
    setConfirmOpen(true);
  };

  const doImport = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const endpoint = mode === "relink" ? "relink_tickets_from_pendenze_xlsx" : "import_tickets_storici_xlsx";
      const r = await fetch(`${API}/admin/${endpoint}`, { method: "POST", body: fd, credentials: "include" });
      if (!r.ok) {
        const j = await r.json().catch(() => ({ detail: "Errore" }));
        throw new Error(j.detail || "Errore import");
      }
      const j = await r.json();
      if (mode === "relink") {
        toast.success(`Ricollegati ${j.aggiornati} ticket · ${j.ancora_orfani} ancora orfani${j.numeri_non_trovati_in_db ? ` · ${j.numeri_non_trovati_in_db} non trovati in DB` : ""}`);
      } else {
        toast.success(`Import OK: ${j.ticket_importati} ticket, ${j.aggiornamenti_importati} aggiornamenti`);
      }
      await onDone();
      setConfirmOpen(false); setPendingFile(null);
    } catch (err) {
      toast.error(`Errore: ${err.message || err}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" onChange={handleFile} className="hidden" data-testid="import-tickets-storici-input" />
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="btn-import-tickets-storici" className="border-green-600 text-green-800 hover:bg-green-50">
        <Upload className="w-4 h-4 mr-1" /> Importa Ticket Storici
      </Button>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Importa / Ricollega ticket storici</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-700">File: <span className="font-mono">{pendingFile?.name}</span></div>
            <div>
              <Label className="text-sm">Modalità</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setMode("relink")}
                  className={`flex-1 px-3 py-2 rounded border text-sm text-left ${mode==="relink"?"bg-emerald-700 text-white border-emerald-700":"bg-white border-slate-300"}`}
                  data-testid="btn-mode-relink"
                >
                  <div className="font-semibold">Solo ricollega anagrafica ✓</div>
                  <div className="text-[11px] opacity-80">Non cancella ticket. Ri-aggancia solo cliente_id / stabilimento_id in base al file. Uso consigliato se i nomi cliente sono spariti dal cruscotto.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setMode("replace")}
                  className={`flex-1 px-3 py-2 rounded border text-sm text-left ${mode==="replace"?"bg-red-700 text-white border-red-700":"bg-white border-slate-300"}`}
                  data-testid="btn-mode-replace"
                >
                  <div className="font-semibold">Sostituisci tutto ⚠</div>
                  <div className="text-[11px] opacity-80">Cancella TUTTI i ticket + interventi/rapporti/consuntivi e re-importa da zero. Solo per primo import.</div>
                </button>
              </div>
            </div>
            {mode === "replace" && (
              <div className="text-xs bg-red-50 border border-red-300 text-red-900 rounded p-2">
                <strong>Attenzione:</strong> elimina tutti i ticket esistenti (compresi interventi, rapporti, consuntivi, fatture, aggiornamenti, documenti).
              </div>
            )}
            {mode === "relink" && (
              <div className="text-xs bg-emerald-50 border border-emerald-300 text-emerald-900 rounded p-2">
                <strong>Sicuro:</strong> aggiorna solo il collegamento cliente/stabilimento. Stato, descrizione, aggiornamenti e tutto il resto restano invariati.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConfirmOpen(false); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={doImport} disabled={busy} data-testid="btn-import-tickets-confirm" className={mode==="replace" ? "bg-red-700 hover:bg-red-800 text-white" : "bg-emerald-700 hover:bg-emerald-800 text-white"}>
              {busy ? "In corso…" : (mode === "relink" ? "Ricollega" : "Cancella tutto e importa")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AnagrafichePage() {
  const { data, reload, canEdit } = useDemo();
  const editable = canEdit("ticket");
  const [tab, setTab] = useState("clienti");
  const [editItem, setEditItem] = useState(null);
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim().toLowerCase();

  const filterRows = (rows) => {
    if (!q) return rows;
    return rows.filter(r => {
      const hay = Object.values(r).map(v => (v == null ? "" : String(v))).join(" ").toLowerCase();
      // Aggiungi anche denominazioni collegate (cliente ragione sociale, stabilimento nome)
      const cli = r.cliente_id && data.clienti.find(c => c.id === r.cliente_id);
      const stab = r.stabilimento_id && data.stabilimenti.find(s => s.id === r.stabilimento_id);
      const extra = `${cli?.ragione_sociale || ""} ${stab?.nome || ""}`.toLowerCase();
      return hay.includes(q) || extra.includes(q);
    });
  };
  const [openDialog, setOpenDialog] = useState(false);

  const crud = {
    clienti: useCrud("clienti", reload),
    stabilimenti: useCrud("stabilimenti", reload),
    contatti: useCrud("contatti", reload),
    tecnici: useCrud("tecnici", reload),
  };

  const clienteOpts = data.clienti.map(c => ({ value: c.id, label: c.ragione_sociale }));
  const stabOpts = data.stabilimenti.map(s => ({ value: s.id, label: `${data.clienti.find(c=>c.id===s.cliente_id)?.ragione_sociale} · ${s.nome}` }));

  const cfg = {
    clienti: {
      title: "Cliente",
      fields: [
        { key: "codice", label: "Codice cliente", required: true },
        { key: "ragione_sociale", label: "Nome cliente / gruppo", required: true },
        { key: "paese_riferimento", label: "Paese riferimento (ISO)" },
        { key: "area", label: "Area (WE / EE / OC / SA / NA / ME / AF)" },
        { key: "stato_cliente", label: "Stato" },
        { key: "attivo", label: "Attivo", type: "switch" },
      ],
      cols: [
        { key: "codice", label: "Codice" },
        { key: "ragione_sociale", label: "Ragione sociale" },
        { key: "paese_riferimento", label: "Paese" },
        { key: "area", label: "Area" },
        { key: "stato_cliente", label: "Stato" },
        { key: "attivo", label: "Attivo", render: v => v ? "Sì" : "No" },
      ],
    },
    stabilimenti: {
      title: "Stabilimento",
      fields: [
        { key: "cliente_id", label: "Cliente", type: "select", options: clienteOpts, required: true },
        { key: "codice_stabilimento", label: "Codice stabilimento" },
        { key: "codice_completo", label: "Codice completo" },
        { key: "nome", label: "Nome stabilimento", required: true },
        { key: "ragione_sociale", label: "Ragione sociale stabilimento" },
        { key: "piva", label: "P.IVA / VAT" },
        { key: "indirizzo", label: "Indirizzo" },
        { key: "cap", label: "CAP / Postcode" },
        { key: "citta", label: "Città" },
        { key: "provincia", label: "Provincia / Stato / Regione" },
        { key: "paese_iso", label: "Paese ISO (es. ITA)" },
        { key: "paese", label: "Paese (descrizione)" },
        { key: "area", label: "Area" },
      ],
      cols: [
        { key: "codice_completo", label: "Codice" },
        { key: "nome", label: "Nome" },
        { key: "cliente_id", label: "Cliente", render: v => data.clienti.find(c=>c.id===v)?.ragione_sociale },
        { key: "citta", label: "Città" },
        { key: "provincia", label: "Prov." },
        { key: "paese_iso", label: "Paese" },
        { key: "area", label: "Area" },
      ],
    },
    contatti: {
      title: "Contatto",
      fields: [
        { key: "nome", label: "Nome (completo)", required: true },
        { key: "cognome", label: "Cognome (opz.)" },
        { key: "livello", label: "Livello (di stabilimento / proprietario…)" },
        { key: "ruolo", label: "Ruolo" },
        { key: "email", label: "Email" },
        { key: "telefono", label: "Telefono" },
        { key: "principale", label: "Principale", type: "switch" },
        { key: "attivo", label: "Attivo", type: "switch" },
        { key: "note", label: "Note" },
        { key: "cliente_id", label: "Cliente (opz.)", type: "select", options: [{value:"",label:"—"}, ...clienteOpts] },
        { key: "stabilimento_id", label: "Stabilimento (opz.)", type: "select", options: [{value:"",label:"—"}, ...stabOpts] },
      ],
      cols: [
        { key: "nome", label: "Nome" },
        { key: "ruolo", label: "Ruolo" },
        { key: "email", label: "Email" },
        { key: "telefono", label: "Telefono" },
        { key: "cliente_id", label: "Cliente", render: v => data.clienti.find(c=>c.id===v)?.ragione_sociale || "" },
        { key: "principale", label: "Princ.", render: v => v ? "★" : "" },
      ],
    },
    tecnici: {
      title: "Tecnico",
      fields: [
        { key: "nome", label: "Nome", required: true },
        { key: "email", label: "E-mail", type: "email" },
        { key: "tipo", label: "Tipo", type: "select", options: [{value:"interno",label:"Interno"},{value:"esterno",label:"Esterno"}] },
        { key: "sede", label: "Sede", type: "select", options: [{value:"Perugia",label:"Perugia"},{value:"Treviso",label:"Treviso"},{value:"Milano",label:"Milano"},{value:"Novara",label:"Novara"},{value:"Pieri",label:"Pieri"}] },
        { key: "competenza", label: "Competenza", type: "select", options: [{value:"SW",label:"SW"},{value:"HW",label:"HW"},{value:"SW+HW",label:"SW+HW"}] },
      ],
      cols: [
        { key: "nome", label: "Nome" },
        { key: "email", label: "E-mail" },
        { key: "tipo", label: "Tipo" },
        { key: "sede", label: "Sede" },
        { key: "competenza", label: "Competenza" },
      ],
    },
  };

  const cur = cfg[tab];
  const rows = data[tab] || [];

  const save = async (form) => {
    if (editItem?.id) await crud[tab].update(editItem.id, form);
    else await crud[tab].create(form);
    setOpenDialog(false); setEditItem(null);
  };

  const remove = async (item) => {
    if (tab === "clienti") {
      // Disattiva anziché cancellare se ha ticket
      const used = data.tickets.some(t => t.cliente_id === item.id);
      if (used) {
        await crud.clienti.update(item.id, { ...item, attivo: false });
        toast.info("Cliente disattivato (ha ticket collegati)");
        return;
      }
    }
    if (!window.confirm("Confermi l'eliminazione?")) return;
    await crud[tab].remove(item.id);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm text-slate-500">Gestione clienti, stabilimenti, contatti e tecnici</div>
        <div className="flex gap-2 flex-wrap">
          {(profile => profile.perm === "admin" || profile.perm === "elisa" || profile.perm === "OWNER" || profile.perm === "SERVICE_MANAGER")(useDemo().profile) && (
            <>
              <ImportAnagraficaButton onDone={reload} />
              <ImportContattiButton onDone={reload} />
              <ImportTicketStoriciButton onDone={reload} />
            </>
          )}
          <FeedbackButton schermata="Anagrafiche" />
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="clienti" data-testid="tab-clienti">Clienti</TabsTrigger>
          <TabsTrigger value="stabilimenti" data-testid="tab-stabilimenti">Stabilimenti</TabsTrigger>
          <TabsTrigger value="contatti" data-testid="tab-contatti">Contatti</TabsTrigger>
          <TabsTrigger value="tecnici" data-testid="tab-tecnici">Tecnici</TabsTrigger>
        </TabsList>
        {["clienti","stabilimenti","contatti","tecnici"].map(k => (
          <TabsContent key={k} value={k}>
            <Card className="emmepi-card p-3 mt-2">
              <div className="flex justify-end mb-3">
                {editable && (
                  <Button size="sm" onClick={() => { setEditItem(null); setOpenDialog(true); }} data-testid={`btn-add-${k}`}>
                    <Plus className="w-4 h-4 mr-1" /> Nuovo {cfg[k].title.toLowerCase()}
                  </Button>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {cfg[k].cols.map(c => <TableHead key={c.key}>{c.label}</TableHead>)}
                    {editable && <TableHead className="w-[100px]">Azioni</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterRows(data[k]||[]).map(r => (
                    <TableRow key={r.id}>
                      {cfg[k].cols.map(c => <TableCell key={c.key}>{c.render ? c.render(r[c.key]) : r[c.key]}</TableCell>)}
                      {editable && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => { setEditItem(r); setOpenDialog(true); }}><Pencil className="w-4 h-4" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {filterRows(data[k]||[]).length === 0 && <TableRow><TableCell colSpan={cfg[k].cols.length+1} className="text-center text-slate-500 py-6">{q ? `Nessun risultato per "${q}"` : "Nessun elemento"}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
      <EntityDialog open={openDialog} onOpenChange={setOpenDialog} title={editItem ? `Modifica ${cur.title}` : `Nuovo ${cur.title}`}
        fields={cur.fields} initial={editItem || {}} onSave={save} />
    </div>
  );
}
